import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, X, ChevronDown, ChevronUp, ArrowRight, 
  Zap, Shield, CreditCard, HelpCircle, Users, Lock,
  Sparkles, Clock, RefreshCw, Building2,
  TrendingUp, ShoppingBag, BarChart2, Target, 
  Calculator, Info
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';
import { useSignupConfig } from '../../contexts/SignupConfigContext';

// =============================================================================
// PRICING DATA: Usage-based SaaS billing, legally compliant
// =============================================================================

const plans = [
  {
    name: 'Starter',
    price: '₹999',
    annualMonthly: '₹833',
    annualTotal: '₹9,990',
    period: '/mo + GST',
    included: '₹500 usage/mo included',
    pricing: { tryon: 24, size: 3, moments: 10 },
    inheritedFrom: null,
    cta: 'Start Free Trial',
    ctaLink: '/partners/register',
    highlighted: false,
    badge: null,
    features: [
      'Shareable Try-On links',
      'Basic analytics dashboard',
      'Email and community support',
      '30-day logs retention',
      'Up to 50 SKUs storage in Nanokart inventory',
    ],
  },
  {
    name: 'Growth',
    price: '₹2,999',
    annualMonthly: '₹2,499',
    annualTotal: '₹29,990',
    period: '/mo + GST',
    included: '₹1,500 usage/mo included',
    pricing: { tryon: 22, size: 2, moments: 9 },
    inheritedFrom: 'Starter',
    cta: 'Upgrade to Growth',
    ctaLink: '/partners/register',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      'Priority API processing',
      'Custom watermark control',
      'Shopify + WooCommerce',
      'Preloaded Try-On links',
      'Up to 3 team members',
      '90-day logs and full audit',
      'Up to 500 SKUs storage in Nanokart inventory',
    ],
  },
  {
    name: 'Professional',
    price: '₹7,999',
    annualMonthly: '₹6,666',
    annualTotal: '₹79,990',
    period: '/mo + GST',
    included: '₹3,000 usage/mo included',
    pricing: { tryon: 20, size: 1.5, moments: 8 },
    inheritedFrom: 'Growth',
    cta: 'Go Professional',
    ctaLink: '/partners/register',
    highlighted: false,
    badge: null,
    features: [
      'Full API, no watermark',
      'Advanced analytics dashboard',
      {
        text: 'SmartGate: Customer Wallet',
        smartgate: true,
      },
      '180-day logs and full audit',
      'Up to 5 team members',
      'Unlimited SKUs storage in Nanokart inventory',
    ],
  },
];

// Top-up packs for "Add Usage" section
const topupPacks = [
  { pay: 1000, get: 1000, bonus: 0, popular: false, label: 'Starter Pack' },
  { pay: 5000, get: 5500, bonus: 10, popular: true, label: 'Growth Pack' },
  { pay: 10000, get: 11500, bonus: 15, popular: false, label: 'Scale Pack' }
];

const comparisonFeatures = [
  { name: 'Parent SKU Limit',            starter: '10 SKUs',   growth: '25 SKUs',   professional: '100 SKUs',  enterprise: 'Unlimited' },
  { name: 'Watermark Control',           starter: false,       growth: true,        professional: true,        enterprise: true },
  { name: 'Priority Processing',         starter: false,       growth: true,        professional: true,        enterprise: 'Dedicated Lane' },
  { name: 'Shopify / WooCommerce',       starter: false,       growth: true,        professional: true,        enterprise: true },
  { name: 'Preloaded Try-On Links',      starter: false,       growth: true,        professional: true,        enterprise: true },
  { name: 'Analytics Dashboard',         starter: 'Basic',     growth: 'Standard',  professional: 'Advanced',  enterprise: 'Advanced + Exports' },
  { name: 'Logs Retention',              starter: '30 days',   growth: '90 days',   professional: '180 days',  enterprise: '365 days' },
  { name: 'Team Members',                starter: '1',         growth: '3',         professional: '5',         enterprise: '10+' },
  { name: 'Rate Limits (SKU/Customer)',  starter: false,       growth: true,        professional: true,        enterprise: true },
  { name: 'Loyalty Engine',             starter: false,       growth: false,       professional: true,        enterprise: true },
  { name: 'SLA',                         starter: false,       growth: false,       professional: false,       enterprise: '99.9%' },
  { name: 'Dedicated Support',           starter: false,       growth: false,       professional: false,       enterprise: true },
];

const faqs = [
  {
    question: 'What counts as 1 SKU?',
    answer: '1 SKU = 1 parent product (one design/style), regardless of how many color or size variants it has. For example, a dress available in 3 colors × 5 sizes = 1 SKU, not 15. So a 25-SKU plan supports 25 different dress styles. If you have 10 designs each in 5 colors, that\'s still just 10 SKUs.'
  },
  {
    question: 'What is Usage Balance?',
    answer: 'Usage Balance is prepaid service value that can be used only within Nanokart.ai to access features. It is non-transferable and non-withdrawable. Think of it as a service credit, not stored money.'
  },
  {
    question: 'What happens when my usage balance runs out?',
    answer: 'Your service continues uninterrupted. You can add more usage balance anytime via the "Add Usage" packs starting at ₹1,000. Larger packs give you bonus usage balance (up to +15%).'
  },
  {
    question: 'Can I add more usage balance?',
    answer: 'Yes! You can add usage balance to your account anytime. The minimum top-up is ₹1,000. Larger packs offer bonuses: ₹5,000 → get ₹5,500 (+10%), ₹10,000 → get ₹11,500 (+15%).'
  },
  {
    question: 'Does unused usage balance roll over?',
    answer: 'Monthly included usage balance from your subscription refreshes each billing cycle and does not roll over. However, any additional usage balance you purchase separately is valid for up to 12 months.'
  },
  {
    question: 'Is GST mandatory?',
    answer: 'Yes, 18% GST is applicable on all subscription plans and usage balance purchases as per Indian tax regulations. The prices shown are exclusive of GST.'
  },
  {
    question: 'Can I upgrade or downgrade anytime?',
    answer: 'Yes, you can change your plan at any time. When upgrading, you will be charged the prorated amount for the remaining billing period. When downgrading, the change takes effect at the start of your next billing cycle.'
  },
  {
    question: 'Is there a free trial?',
    answer: 'Yes! Every new account starts with ₹500 free usage balance for 14 days. No credit card required. You can test Virtual Try-On and Moments APIs without any commitment.'
  },
  {
    question: 'How does repeat try-on caching work?',
    answer: 'When the same customer tries on the same garment again, we serve the cached result at zero cost. This is a major cost saver for high-traffic stores. This applies to all plans.'
  }
];

// =============================================================================
// COMPONENTS
// =============================================================================

const PlanCard = ({ plan, index, isAnnual }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    viewport={{ once: true }}
    className={`relative rounded-2xl flex flex-col ${
      plan.highlighted
        ? 'bg-slate-900 ring-2 ring-blue-500 shadow-2xl shadow-blue-500/15'
        : 'bg-white border border-slate-200 shadow-sm'
    }`}
  >
    {plan.badge && (
      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-xs font-bold rounded-full whitespace-nowrap shadow-lg">
        {plan.badge}
      </div>
    )}

    {/* Header: name + price */}
    <div className="p-6 pb-4">
      <p className={`text-sm font-semibold mb-3 ${plan.highlighted ? 'text-blue-400' : 'text-slate-500'}`}>
        {plan.name}
      </p>
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <span className={`text-4xl font-black ${plan.highlighted ? 'text-white' : 'text-slate-900'}`}>
          {isAnnual ? plan.annualMonthly : plan.price}
        </span>
        <span className={`text-sm ${plan.highlighted ? 'text-slate-500' : 'text-slate-400'}`}>/mo</span>
        {isAnnual && (
          <span className={`text-sm line-through ${plan.highlighted ? 'text-slate-600' : 'text-slate-400'}`}>
            {plan.price}
          </span>
        )}
      </div>
      {isAnnual ? (
        <p className={`text-xs mb-1 ${plan.highlighted ? 'text-slate-400' : 'text-slate-500'}`}>
          Billed annually at {plan.annualTotal} + GST
        </p>
      ) : (
        <p className={`text-xs mb-1 ${plan.highlighted ? 'text-slate-500' : 'text-slate-400'}`}>
          + GST · billed monthly
        </p>
      )}
      <p className={`text-xs font-semibold ${plan.highlighted ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {plan.included}
      </p>
    </div>

    {/* CTA */}
    <div className="px-6 pb-5">
      <Link
        to={plan.ctaLink}
        className={`block w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all ${
          plan.highlighted
            ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-md shadow-blue-600/30'
            : 'bg-slate-900 text-white hover:bg-slate-700'
        }`}
      >
        {plan.cta}
      </Link>
    </div>

    <div className={`mx-6 border-t ${plan.highlighted ? 'border-slate-700' : 'border-slate-100'}`} />

    {/* Cost per use: compact 3-col */}
    <div className="px-6 py-4">
      <p className={`text-xs uppercase tracking-widest font-medium mb-3 ${plan.highlighted ? 'text-slate-500' : 'text-slate-400'}`}>
        Cost per use
      </p>
      <div className="grid grid-cols-3 gap-2 text-center mb-2">
        {[
          { label: 'Try-On', val: plan.pricing.tryon },
          { label: 'Size Rec', val: plan.pricing.size },
          { label: 'Moments', val: plan.pricing.moments },
        ].map(({ label, val }) => (
          <div key={label} className={`rounded-lg py-2 ${plan.highlighted ? 'bg-slate-800' : 'bg-slate-50'}`}>
            <p className={`text-xs mb-0.5 ${plan.highlighted ? 'text-slate-500' : 'text-slate-400'}`}>{label}</p>
            <p className={`text-sm font-bold ${plan.highlighted ? 'text-white' : 'text-slate-800'}`}>
              ₹{val}
            </p>
          </div>
        ))}
      </div>
    </div>

    <div className={`mx-6 border-t ${plan.highlighted ? 'border-slate-700' : 'border-slate-100'}`} />

    {/* Features */}
    <div className="px-6 py-5 flex-1">
      {plan.inheritedFrom && (
        <p className={`text-xs font-semibold mb-3 ${plan.highlighted ? 'text-slate-400' : 'text-slate-500'}`}>
          Everything in {plan.inheritedFrom}, and:
        </p>
      )}
      <ul className="space-y-2.5">
        {plan.features.map((feature, i) => {
          const isString = typeof feature === 'string';
          const text = isString ? feature : feature.text;
          const hasSKU = isString && feature.includes('SKUs storage');
          const isLocked = !isString && feature.locked;
          const isSmartgate = !isString && feature.smartgate;

          // Locked SmartGate row (Starter / Growth)
          if (isLocked) {
            return (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-300" />
                <span className={plan.highlighted ? 'text-slate-600' : 'text-slate-300'}>
                  {text}
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-600 rounded-full align-middle">Pro only</span>
                </span>
              </li>
            );
          }

          // SmartGate highlighted row (Professional)
          if (isSmartgate) {
            return (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <Shield className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" />
                <span>
                  <span className={`font-semibold ${plan.highlighted ? 'text-white' : 'text-slate-800'}`}>
                    {text}
                  </span>
                  {feature.detail && (
                    <span className={`block text-xs mt-0.5 ${plan.highlighted ? 'text-slate-500' : 'text-slate-400'}`}>
                      {feature.detail}
                    </span>
                  )}
                </span>
              </li>
            );
          }

          // Standard string feature
          return (
            <li key={i} className={`flex items-start gap-2.5 text-sm ${plan.highlighted ? 'text-slate-300' : 'text-slate-600'}`}>
              <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-blue-400' : 'text-emerald-500'}`} />
              <span>
                {text}
                {hasSKU && (
                  <span className="relative inline-block ml-1 align-middle group">
                    <HelpCircle className={`w-3.5 h-3.5 inline cursor-help ${plan.highlighted ? 'text-slate-600' : 'text-slate-400'}`} />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 p-2.5 bg-slate-900 border border-slate-700 text-white text-xs rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none z-30 transition-opacity leading-relaxed">
                      <strong>1 parent product = 1 SKU</strong><br />
                      A dress in 3 colors &amp; 5 sizes = 1 SKU.
                    </span>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  </motion.div>
);

// =============================================================================
// ANIMATED NUMBER: smooth counter animation
// =============================================================================
const AnimatedNumber = ({ value, prefix = '', suffix = '', decimals = 0 }) => {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const start = prevRef.current;
    const end = value;
    if (start === end) return;
    const duration = 400;
    const startTime = performance.now();
    const raf = (ts) => {
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * ease;
      setDisplay(decimals > 0 ? +current.toFixed(decimals) : Math.round(current));
      if (progress < 1) requestAnimationFrame(raf);
      else prevRef.current = end;
    };
    requestAnimationFrame(raf);
  }, [value, decimals]);

  const formatted = typeof display === 'number' && !isNaN(display)
    ? (decimals > 0 ? display.toFixed(decimals) : display.toLocaleString('en-IN'))
    : display;

  return <>{prefix}{formatted}{suffix}</>;
};

// =============================================================================
// SLIDER COMPONENT
// =============================================================================
const RangeSlider = ({ min, max, step = 1, value, onChange, label, valueDisplay, color = 'blue' }) => {
  const pct = ((value - min) / (max - min)) * 100;
  const colors = {
    blue: { track: '#3b82f6', thumb: '#2563eb' },
    emerald: { track: '#10b981', thumb: '#059669' },
    violet: { track: '#8b5cf6', thumb: '#7c3aed' },
    amber: { track: '#f59e0b', thumb: '#d97706' },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-sm text-slate-400">{label}</span>
        <span className="text-sm font-bold text-white">{valueDisplay}</span>
      </div>
      <div className="relative h-3 rounded-full bg-slate-800">
        <div
          className="absolute h-3 rounded-full transition-all duration-150"
          style={{ width: `${pct}%`, background: c.track, boxShadow: `0 0 10px ${c.track}70` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-3 opacity-0 cursor-pointer"
          style={{ WebkitAppearance: 'none' }}
        />
        <div
          className="absolute w-5 h-5 rounded-full border-2 border-white shadow-lg -top-1 -translate-x-1/2 pointer-events-none transition-all duration-150"
          style={{ left: `${pct}%`, background: c.thumb, boxShadow: `0 0 12px ${c.thumb}90` }}
        />
      </div>
      <div className="flex justify-between mt-1.5 text-xs text-slate-700">
        <span>{min}</span><span>{max}</span>
      </div>
    </div>
  );
};

// =============================================================================
// ROI CALCULATOR COMPONENT
// =============================================================================
const ROI_PLANS = {
  starter:      { name: 'Starter',      price: 999,  annualPrice: 833,  included: 500,  tryonCost: 24 },
  growth:       { name: 'Growth ⭐',    price: 2999, annualPrice: 2499, included: 1500, tryonCost: 22 },
  professional: { name: 'Professional', price: 7999, annualPrice: 6666, included: 3000, tryonCost: 20 },
};

const fmt = (n) => '₹' + Math.round(n).toLocaleString('en-IN');

const ROICalculator = () => {
  const [tryons, setTryons]               = useState(300);
  const [planKey, setPlanKey]             = useState('growth');
  const [conversionBoost, setConversionBoost] = useState(3);
  const [aov, setAov]                     = useState(1500);
  const [returnRate, setReturnRate]       = useState(20);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [annual, setAnnual]               = useState(false);

  const plan = ROI_PLANS[planKey];

  const calc = useMemo(() => {
    const basePrice       = annual ? plan.annualPrice : plan.price;
    const usageConsumed   = tryons * plan.tryonCost;
    const additionalUsage = Math.max(0, usageConsumed - plan.included);
    const nanokartCost    = basePrice + additionalUsage;
    const extraBuyers     = tryons * (conversionBoost / 100);
    const extraRevenue    = extraBuyers * aov;
    const returnSavings   = extraBuyers * (returnRate / 100) * aov * 0.30;
    const totalGain       = extraRevenue + returnSavings;
    const netGain         = totalGain - nanokartCost;
    const roiMultiple     = totalGain > 0 ? totalGain / nanokartCost : 0;
    const gainPerTryon    = (aov * conversionBoost / 100) + (aov * returnRate / 100 * 0.30 * conversionBoost / 100);
    const breakEvenTryons = gainPerTryon > 0 ? Math.ceil(nanokartCost / gainPerTryon) : 0;
    const breakEvenPct    = Math.min(100, Math.round((breakEvenTryons / tryons) * 100));
    const mult            = annual ? 12 : 1;
    const profitPerTryon  = tryons > 0 ? (totalGain - nanokartCost) / tryons : 0;
    return {
      nanokartCost: nanokartCost * mult,
      extraBuyers: Math.round(extraBuyers * mult),
      extraRevenue: extraRevenue * mult,
      returnSavings: returnSavings * mult,
      totalGain: totalGain * mult,
      netGain: netGain * mult,
      roiMultiple,
      breakEvenTryons,
      breakEvenPct,
      profitPerTryon,
      usageConsumed,
      includedBalance: plan.included,
    };
  }, [tryons, planKey, conversionBoost, aov, returnRate, annual, plan]);

  const isPositive = calc.netGain > 0;
  const roiLabel   = calc.roiMultiple >= 3 ? 'Outstanding'
                   : calc.roiMultiple >= 2 ? 'Strong'
                   : calc.roiMultiple >= 1 ? 'Positive'
                   : 'Low';

  return (
    <section id="roi-calculator" className="py-16 sm:py-24 bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #3b82f6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #8b5cf6 0%, transparent 50%)' }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-xl mx-auto mb-10 sm:mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-5 border border-blue-500/30">
            <Calculator className="w-3.5 h-3.5" />
            ROI Calculator
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Run the Math. See the Upside.
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            Adjust your numbers. Real projected returns, no fluff.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-5 items-start">

          {/* ─── LEFT: INPUTS ─── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 border border-slate-700/70 rounded-2xl divide-y divide-slate-800 shadow-2xl shadow-black/50"
          >
            {/* Plan */}
            <div className="p-5 sm:p-6">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Plan</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {Object.entries(ROI_PLANS).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => setPlanKey(key)}
                    className={`py-2 rounded-lg text-xs font-semibold transition-all border ${
                      planKey === key
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-slate-800 border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                    }`}
                    data-testid={`plan-${key}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="text-white font-semibold">
                  {fmt(annual ? plan.annualPrice : plan.price)}
                  <span className="text-slate-500 font-normal">/mo</span>
                  {annual && <span className="ml-1 text-emerald-400 font-normal text-xs">annual</span>}
                </span>
                <span><span className="text-emerald-400 font-semibold">{fmt(plan.included)}</span> included</span>
                <span>VTO: <span className="text-blue-400 font-semibold">{fmt(plan.tryonCost)}/call</span></span>
              </div>
            </div>

            {/* Sliders */}
            <div className="p-5 sm:p-6 space-y-6">
              <RangeSlider
                min={100} max={100000} step={500} value={tryons}
                onChange={setTryons} color="blue"
                label="Monthly Try-Ons"
                valueDisplay={tryons.toLocaleString('en-IN') + '/mo'}
              />
              <RangeSlider
                min={3} max={50} step={0.5} value={conversionBoost}
                onChange={setConversionBoost} color="emerald"
                label="Conversion Uplift"
                valueDisplay={'+' + conversionBoost + '% conversion increase'}
              />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Avg Order Value</span>
                  <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1">
                    <span className="text-slate-500 text-xs">₹</span>
                    <input
                      type="number"
                      value={aov}
                      onChange={e => setAov(Math.max(100, Math.min(1000000, Number(e.target.value) || 100)))}
                      className="w-24 bg-transparent text-white text-sm font-semibold text-right outline-none"
                      step={500}
                      data-testid="aov-input"
                    />
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {[500, 1000, 2000, 5000].map(v => (
                    <button
                      key={v}
                      onClick={() => setAov(v)}
                      className={`flex-1 text-xs py-1 rounded-lg transition-colors ${
                        aov === v ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {fmt(v)}
                    </button>
                  ))}
                </div>
              </div>
              <RangeSlider
                min={5} max={45} step={1} value={returnRate}
                onChange={setReturnRate} color="amber"
                label="Return Rate"
                valueDisplay={returnRate + '% of orders'}
              />
            </div>

            {/* Footer controls */}
            <div className="p-5 sm:p-6 flex items-center justify-between">
              <button
                onClick={() => setShowAssumptions(!showAssumptions)}
                className="text-xs text-slate-600 hover:text-slate-400 flex items-center gap-1 transition-colors"
              >
                <Info className="w-3 h-3" />
                {showAssumptions ? 'Hide' : 'View'} assumptions
              </button>
              <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
                <button
                  onClick={() => setAnnual(false)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${!annual ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >Monthly</button>
                <button
                  onClick={() => setAnnual(true)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${annual ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >Annual</button>
              </div>
            </div>

            <AnimatePresence>
              {showAssumptions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-5 sm:px-6 pb-5 text-xs text-slate-500 space-y-1"
                >
                  <p>• Conversion uplift: % of try-on users who buy due to VTO (min 3%)</p>
                  <p>• Return reduction: 30% of would-be returns prevented by VTO</p>
                  <p>• Nanokart cost = plan price + max(0, usage consumed − included balance)</p>
                  <p>• Prices exclude 18% GST. Based on real D2C brand benchmarks.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ─── RIGHT: RESULTS ─── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden divide-y divide-slate-800"
          >
            {/* ROI Hero */}
            <div className={`px-6 sm:px-8 pt-8 pb-6 text-center ${isPositive ? 'bg-gradient-to-br from-emerald-950/60 to-slate-900' : ''}`}>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">ROI Multiple</p>
              <div className="flex items-baseline justify-center gap-3">
                <span className={`text-5xl sm:text-6xl font-black leading-none tracking-tight ${isPositive ? 'text-emerald-400' : 'text-slate-500'}`}>
                  <AnimatedNumber value={+calc.roiMultiple.toFixed(1)} decimals={1} suffix="x" />
                </span>
              </div>
              <p className="text-slate-600 text-xs mt-4 italic">
                For every ₹1 spent, you get back{' '}
                <span className="text-slate-400 not-italic font-semibold">
                  <AnimatedNumber value={+calc.roiMultiple.toFixed(1)} decimals={1} prefix="₹" />.
                </span>
                {' '}Conservative estimate. Actual results may vary.
              </p>
            </div>

            {/* Break-even */}
            <div className="px-6 sm:px-8 py-5">
              <div className="flex items-center justify-between text-xs mb-2.5">
                <div>
                  <span className="text-slate-400">
                    Break-even at{' '}
                    <span className="text-white font-semibold">{calc.breakEvenTryons.toLocaleString('en-IN')} try-ons</span>
                  </span>
                  <p className="text-slate-600 text-xs mt-0.5">(Every try-on after this adds profit)</p>
                </div>
                <span className="text-slate-600 self-start">{calc.breakEvenPct}%</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${calc.breakEvenPct}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-1.5">
                {calc.breakEvenPct < 100
                  ? `✓ You clear break-even by ${Math.max(0, tryons - calc.breakEvenTryons).toLocaleString('en-IN')} try-ons`
                  : '⚠ Increase try-ons or conversion to reach break-even'}
              </p>
            </div>

            {/* 2-stat strip */}
            <div className="grid grid-cols-2 divide-x divide-slate-800">
              <div className="px-5 sm:px-7 py-4">
                <p className="text-xs text-slate-500 mb-1">Revenue Upside</p>
                <p className="text-lg font-bold text-white">
                  <AnimatedNumber value={Math.round(calc.totalGain)} prefix="₹" />
                </p>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {fmt(Math.round(calc.extraRevenue))} sales · +{fmt(Math.round(calc.returnSavings))} returns
                </p>
              </div>
              <div className="px-5 sm:px-7 py-4">
                <p className="text-xs text-slate-500 mb-1">Nanokart Investment</p>
                <p className="text-lg font-bold text-white">
                  <AnimatedNumber value={Math.round(calc.nanokartCost)} prefix="₹" />
                </p>
                <p className="text-xs text-slate-600 mt-1">plan + usage cost</p>
              </div>
            </div>

            {/* Net Profit */}
            <div className={`px-6 sm:px-8 py-6 text-center ${isPositive ? 'bg-emerald-950/40' : 'bg-slate-800/30'}`}>
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">
                Your Net Profit ({annual ? 'Annual' : 'Monthly'})
              </p>
              <p className={`text-5xl sm:text-6xl font-black tracking-tight ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                <AnimatedNumber value={Math.abs(Math.round(calc.netGain))} prefix="₹" />
              </p>
              <p className="text-xs text-slate-600 mt-2">Revenue Upside − Nanokart Investment</p>
              <p className="text-xs text-emerald-700 mt-1">
                +<AnimatedNumber value={calc.extraBuyers} /> new buyers this {annual ? 'year' : 'month'}
              </p>
            </div>

            {/* CTA */}
            <div className="p-5 sm:p-6">
              <Link
                to="/partners/register"
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold text-sm hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-900/40"
                data-testid="roi-cta-btn"
              >
                <Zap className="w-4 h-4" />
                Start Free: Get ₹500 Usage Instantly
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-center text-xs text-slate-600 mt-2.5">
                Based on real D2C brand benchmarks. Conservative estimates.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

const planDelta = [
  {
    name: 'Starter',
    price: '₹999', period: '/mo + GST',
    cta: 'Get Started', ctaLink: '/partners/register',
    inheritedFrom: null,
    highlighted: false,
    features: [
      'Up to 10 parent SKUs',
      'Virtual Try-On, Size Rec & Moments API',
      'Shareable Try-On links',
      'Basic analytics dashboard',
      'Email & community support',
      '30-day logs retention',
    ],
  },
  {
    name: 'Growth',
    price: '₹2,999', period: '/mo + GST',
    cta: 'Upgrade to Growth', ctaLink: '/partners/register',
    inheritedFrom: 'Starter',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      'Up to 25 parent SKUs + multiple images',
      'Priority API processing',
      'Custom watermark control',
      'Shopify + WooCommerce',
      'Preloaded Try-On links',
      'Up to 3 team members',
      '90-day logs retention',
    ],
  },
  {
    name: 'Professional',
    price: '₹7,999', period: '/mo + GST',
    cta: 'Go Professional', ctaLink: '/partners/register',
    inheritedFrom: 'Growth',
    highlighted: false,
    features: [
      'Up to 100 parent SKUs',
      'Full API, no watermark',
      'Advanced analytics dashboard',
      'Loyalty engine',
      '180-day logs & full audit',
      'Up to 5 team members',
    ],
  },
];

const ComparisonTable = () => (
  <div>
    <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
      {planDelta.map((plan, i) => (
        <motion.div
          key={plan.name}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          viewport={{ once: true }}
          className={`relative rounded-2xl border flex flex-col ${
            plan.highlighted
              ? 'border-blue-500 shadow-xl shadow-blue-100 bg-white'
              : 'border-slate-200 bg-white'
          }`}
        >
          {plan.badge && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
              {plan.badge}
            </div>
          )}

          {/* Header */}
          <div className="p-6 pb-5">
            <p className={`text-xl font-bold mb-0.5 ${plan.highlighted ? 'text-blue-700' : 'text-slate-900'}`}>
              {plan.name}
            </p>
            <div className="flex items-baseline gap-1 mt-3">
              <span className="text-3xl font-black text-slate-900">{plan.price}</span>
              <span className="text-slate-400 text-sm">{plan.period}</span>
            </div>
          </div>

          {/* CTA */}
          <div className="px-6 pb-5">
            <Link
              to={plan.ctaLink}
              className={`block w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all ${
                plan.highlighted
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
              }`}
            >
              {plan.cta}
            </Link>
          </div>

          {/* Divider */}
          <div className="mx-6 border-t border-slate-100" />

          {/* Features */}
          <div className="p-6 pt-5 flex-1">
            {plan.inheritedFrom && (
              <p className="text-xs font-semibold text-slate-500 mb-3">
                Everything in {plan.inheritedFrom}, and:
              </p>
            )}
            <ul className="space-y-2.5">
              {plan.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-blue-500' : 'text-emerald-500'}`} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      ))}
    </div>

    {/* Enterprise footnote */}
    <p className="text-center text-xs text-slate-400 mt-6">
      Need higher limits or custom pricing?{' '}
      <Link to="/contact" className="text-blue-500 hover:text-blue-600 underline underline-offset-2">
        Talk to our sales team
      </Link>
    </p>
  </div>
);

const FAQItem = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="pb-4 pr-8"
        >
          <p className="text-slate-600 text-sm leading-relaxed">{faq.answer}</p>
        </motion.div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN PRICING PAGE
// =============================================================================

const Pricing = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false);
  const { signupCredits } = useSignupConfig();

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, []);

  return (
    <MarketingLayout lightTheme>
      <SEO {...SEO_CONFIGS.pricing} />
      
      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-6 border border-emerald-200">
              <Sparkles className="w-3.5 h-3.5" />
              Free trial: ₹500 usage balance, no credit card required
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Pay only when your customers use it
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-3">
              Every plan includes usage balance. Scale up as you grow.
            </p>
            <p className="text-base text-slate-500">
              If 100 customers try on, even 3-5 extra purchases recover your entire monthly cost.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              All prices exclude 18% GST. Usage balance refreshes every 30 days.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Monthly / Annual toggle */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-slate-900' : 'text-slate-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(v => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none ${isAnnual ? 'bg-blue-600' : 'bg-slate-300'}`}
              aria-label="Toggle billing period"
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300 ${isAnnual ? 'translate-x-6' : ''}`} />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-slate-900' : 'text-slate-400'}`}>
                Annually
              </span>
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                Save 17% · 2 Months Free
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <PlanCard key={plan.name} plan={plan} index={index} isAnnual={isAnnual} />
            ))}
          </div>

          <div className="text-center mt-8 space-y-1.5">
            <p className="text-sm text-slate-400">
              <strong className="text-slate-600">Smart Cache:</strong> Repeat try-ons served from cache at zero cost, a major saving for high-traffic stores.
            </p>
            <p className="text-sm text-slate-400">
              Need higher limits or custom pricing?{' '}
              <Link to="/contact" className="text-blue-500 hover:text-blue-600 underline underline-offset-2 font-medium">
                Talk to our sales team →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Interactive ROI Calculator */}
      <ROICalculator />

      {/* Add Usage (Top-up) Section */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Add Usage: Save more with larger packs
            </h2>
            <p className="text-slate-600">
              Need more usage balance between billing cycles? Top up anytime.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-5">
            {topupPacks.map((pack, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-2xl border-2 p-5 text-center relative ${
                  pack.popular ? 'border-blue-500 shadow-lg' : 'border-slate-200'
                }`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                    SAVE MORE
                  </span>
                )}
                <p className="text-slate-500 text-sm mb-1">{pack.label}</p>
                <p className="text-3xl font-bold text-slate-900">₹{pack.get.toLocaleString()}</p>
                <p className="text-slate-500 text-sm">usage balance</p>
                <p className="text-blue-600 font-semibold mt-2">Pay ₹{pack.pay.toLocaleString()}</p>
                {pack.bonus > 0 && (
                  <p className="text-emerald-600 text-sm font-semibold mt-1">+{pack.bonus}% free bonus</p>
                )}
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Usage Balance is non-transferable and non-withdrawable. It is a prepaid service value valid only within Nanokart.ai.
          </p>
        </div>
      </section>

      {/* How Usage Balance Works */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              How Usage Balance Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Simple, transparent, legally safe
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Zap,
                title: 'Included Every Month',
                description: 'Every paid plan includes usage balance. It\'s like a monthly service credit, automatically applied to your API usage.'
              },
              {
                icon: RefreshCw,
                title: 'Repeat Try-Ons Are Free',
                description: 'Same customer tries the same garment again? Zero cost. Our Smart Cache AI eliminates redundant processing entirely.'
              },
              {
                icon: CreditCard,
                title: 'Add More Anytime',
                description: 'Need more balance mid-month? Top up with packs starting at ₹1,000. Larger packs come with bonus usage balance up to +15%.'
              },
              {
                icon: Shield,
                title: 'Non-withdrawable',
                description: 'Usage Balance is a prepaid service value. It cannot be withdrawn or transferred. It\'s purely for Nanokart.ai API usage.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-50 flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Everything you need to know about our pricing
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
          >
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-20 bg-blue-600">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            viewport={{ once: true }} 
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Start With ₹500 Free Usage. No Card Needed.
            </h2>
            <p className="text-base sm:text-lg text-blue-100 mb-8">
              Every new account starts with ₹500 free usage balance. See the impact on your conversion and return rates within 14 days.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/partners/register" 
                className="w-full sm:w-auto px-8 py-3.5 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg"
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/contact" 
                className="w-full sm:w-auto px-8 py-3.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-400 transition-colors"
              >
                Talk to Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default Pricing;
